import { ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(input: string | number): string {
  const date = new Date(input)
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  })
}

export function absoluteUrl(path: string) {
  return `${process.env.NEXT_PUBLIC_APP_URL}/${path}`
}

export function absoluteUrlWithoutHttp(path?: string) {
  // remove http:// or https:// from the url
  const url = process.env.NEXT_PUBLIC_APP_URL?.replace(/(^\w+:|^)\/\//, '');
  return `${url}/${path}`
}

// Get base url
export function getBaseUrl() {
    return process.env.NEXT_PUBLIC_APP_URL
}

// function to check whether a string is any hex colour
export function checkIsHexColour(colour: string | null, defaultColour?: string | null) {
    if (colour && /(^#[0-9A-F]{6}$)|(^#[0-9A-F]{3}$)/i.test(colour)) {
        return true;
    }
    return false;
}

function hexToRgb(hex: string) {
  var bigint = parseInt(hex.slice(1), 16);
  var r = (bigint >> 16) & 255;
  var g = (bigint >> 8) & 255;
  var b = bigint & 255;
  return { r, g, b };
}

//inputs are objects with r, g, b properties
function midpointColor(color1: { r: number; g: number; b: number }, color2: { r: number; g: number; b: number }) {
  var midR = (color1.r + color2.r) / 2;
  var midG = (color1.g + color2.g) / 2;
  var midB = (color1.b + color2.b) / 2;
  return { r: midR, g: midG, b: midB };
}

function rgbToHex(color: { r: number; g: number; b: number }) {
  var r = Math.round(color.r).toString(16).padStart(2, '0');
  var g = Math.round(color.g).toString(16).padStart(2, '0');
  var b = Math.round(color.b).toString(16).padStart(2, '0');
  return `#${r}${g}${b}`;
}

function increaseBrightness(rgb: { r: number; g: number; b: number }, percent: number) {
  if (rgb.r === 0 && rgb.g === 0 && rgb.b === 0) {
    // Special case for black
    return { r: percent, g: percent, b: percent };
  }

  var r = Math.min(255, rgb.r * (1 + percent / 100));
  var g = Math.min(255, rgb.g * (1 + percent / 100));
  var b = Math.min(255, rgb.b * (1 + percent / 100));
  return { r, g, b };
}

function decreaseBrightness(rgb: { r: number; g: number; b: number }, percent: number) {
  var r = Math.max(0, rgb.r * (1 - percent/100));
  var g = Math.max(0, rgb.g * (1 - percent/100));
  var b = Math.max(0, rgb.b * (1 - percent/100));
  return { r, g, b };
}

export function getMidpointHexColor(hex1: string, hex2: string) {
  var color1 = hexToRgb(hex1);
  var color2 = hexToRgb(hex2);
  var midpoint = midpointColor(color1, color2);
  return rgbToHex(midpoint);
}

export function createGradient(hex: string, percent: number) {
  var rgb = hexToRgb(hex);
  var lighter = increaseBrightness(rgb, percent);
  var darker = decreaseBrightness(rgb, percent);
  return {
      lighter: rgbToHex(lighter),
      darker: rgbToHex(darker)
  };
}

export function determineTextColor(backgroundColor: string): string {
  const r = parseInt(backgroundColor.substring(1, 2), 16);
  const g = parseInt(backgroundColor.substring(3, 2), 16);
  const b = parseInt(backgroundColor.substring(5, 2), 16);
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  const switchToWhite = 190;
  return (yiq >= switchToWhite) ? '#000000' : '#FFFFFF';
}

// function that returns a shade of the colour
export function shadeColor(colour: string | null, percent: number) {
    if (!colour) {
        colour = '#176CEB';
    } 
    const R = parseInt(colour.substring(1, 3), 16);
    const G = parseInt(colour.substring(3, 5), 16);
    const B = parseInt(colour.substring(5, 7), 16);
  
    const getHex = (c: number) => {
      const hex = c.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
  
    const calcShade = (colorComponent: number) => {
      return Math.min(Math.max(0, Math.floor(colorComponent * (1 + percent / 100))), 255);
    };
  
    return '#' + getHex(calcShade(R)) + getHex(calcShade(G)) + getHex(calcShade(B));
}

export function reorder<T>(list: T[], startIndex: number, endIndex: number): T[] {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
}

export const initials = (text: string) => {
  if (text === '') return '';
  const isNumerical = text.split('').every(char => char >= '0' && char <= '9');
  return isNumerical ? '#' : text.replaceAll(/[^a-zA-Z \-]/g, '').split(/[\s\-]/).map((w) => w[0]).join('').substring(0, 2);
}

export const stringToColor = (string: string) => {
  /* eslint-disable no-bitwise */
  const hash = Array.from(string).reduce((acc, char) => char.charCodeAt(0) + ((acc << 5) - acc), 0);

  let color = '#';

  for (let i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
}


